<template>
  <!-- On the therapist web build the two marks are real buttons, so the bar
       can't stay hidden from assistive tech. The native patient app keeps the
       purely decorative bar it shipped as. -->
  <header :aria-hidden="isWeb ? undefined : true">
    <!-- Web/therapist: the wordmark is a home button — back to the start screen
         with the patient context dropped. Native/patient: decorative. -->
    <button v-if="isWeb" type="button" class="lingotalk" :title="t('home')" @click="goHome">
      <img :src="logoWordmark" alt="LingoTalk Logo" />
    </button>
    <picture v-else class="lingotalk">
      <img :src="logoWordmark" alt="LingoTalk Logo" />
    </picture>

    <!-- Therapist chrome only. WorkspaceLayout is shared with the native
         patient app, whose header stays the single-logo bar it shipped as. -->
    <PortalButton v-if="isWeb" />
  </header>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import PortalButton from '@/components/PortalButton.vue'
import { isWeb } from '@/lib/platform'
import { useActivePatientStore } from '@/stores/activePatient'

import logoWordmark from '@/assets/logo/light/wordmark.svg?no-inline'

const { t } = useI18n()
const router = useRouter()

/**
 * "Back to start, nothing selected." Drops the locked-in patient so
 * LaunchInitial greets with "Willkommen bei LingoTalk!" rather than
 * "LingoTalk für <Name>", then hops to the therapist start screen.
 *
 * Clearing before the push is safe: the one screen that bounces on an empty
 * patient does so in `onMounted` (ExercisesPatient), not in a watcher, so it
 * can't race us into the chooser on its way out.
 */
function goHome(): void {
  useActivePatientStore().clear()
  void router.push({ name: 'launch-initial' })
}
</script>

<style scoped lang="css">
header {
  width: 100dvw;
  max-width: 1440px;
  /* Deterministic height so per-view title bars can stick at --app-header-height
     (defined in resources/css/app.css) and not overlap us. */
  height: var(--app-header-height);

  display: flex;
  /* Logo left, digola mark right. With the digola picture absent (patient
     build) the lone wordmark still sits hard-left, as before. */
  justify-content: space-between;
  align-items: center;

  background: var(--background);
  border-bottom: 1px solid var(--black);

  margin: 0 auto;
  /* Pad the top by the safe-area inset so the system status bar
     (iOS Dynamic Island, Android status bar) sits above the logo rather
     than on top of it. Sourced from --safe-area-inset-top so both iOS's
     native env() and the Android native bridge flow through. */
  padding: var(--safe-area-inset-top) 0.55rem 0;

  position: sticky;
  top: 0;
  /* Above per-view sticky chrome so the logo row always wins when both
     would otherwise paint at the same scroll offset. */
  z-index: 10;

  /* `picture` on native, `button` on web — same box either way, so the two
     builds render the bar identically. The digola mark on the right is
     <PortalButton>, which carries its own sizing and hover chrome (the same
     as below), so these rules stay on the wordmark. */
  picture.lingotalk,
  button.lingotalk {
    /* Never let the flex row shrink a logo away. */
    flex: 0 0 auto;

    width: auto;
    height: auto;

    img {
      /* Pin the HEIGHT and let the aspect ratio drive the width. wordmark.svg
         carries only a viewBox (no width/height attributes), so it has no
         definite intrinsic width for a percentage to resolve against — with a
         second logo in the row that collapsed it to nothing. */
      width: auto;
      height: 37px;

      /* Kill the inline-image baseline gap inside the button box, which would
         otherwise push the mark off-centre against the picture rendering. */
      display: block;
    }
  }

  /* The logo button carries no button chrome — just the mark, plus the same
     rounded hover shadow the back/help buttons use (Button.vue). The negative
     margins cancel the hover padding so the mark stays optically where it
     sat before it became clickable. */
  button.lingotalk {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;

    @media (hover: hover) and (pointer: fine) {
      margin: 0 -0.5rem;
      padding: 0.25rem 0.5rem;

      &:hover {
        border-radius: 15px;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }
}
</style>

<i18n>
{
  "de": {
    "home": "Zur Startseite"
  },
  "en": {
    "home": "Back to start"
  }
}
</i18n>
